import { App } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { botLink, channelApi, channelQueryKeys, type ChannelCard } from '@entities/channel';
import { extractErrorMessage } from '@shared/api';
import { num } from '@shared/ui/v2';
import { ChBadge, StatusPill, dateRu } from './chUi';

/** Экран 6б «Страница бота» (Telegram / MAX), утверждён 28.10.2026. */
export function BotPage({ assistantId, c }: { assistantId: string; c: ChannelCard }) {
  const { modal, message } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const tg = c.platform === 'telegram';
  const where = tg ? 'Telegram' : 'MAX';
  const link = botLink(c);
  const on = c.status === 'active';

  const refresh = () => {
    qc.invalidateQueries({ queryKey: channelQueryKeys.all });
    qc.invalidateQueries({ queryKey: ['cabinet'] });
  };

  const toggle = async () => {
    if (c.status === 'disabled') return;
    setBusy(true);
    try {
      await channelApi.update(c.id, { status: on ? 'paused' : 'active' });
      refresh();
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = () =>
    modal.confirm({
      title: `Отключить ${c.title}?`,
      content:
        'Бот перестанет отвечать, а переписка с ним удалится из «Диалогов». Подключить снова можно в любой момент — статистика начнётся с нуля.',
      okText: 'Отключить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await channelApi.delete(c.id);
          refresh();
          navigate(`/assistants/${assistantId}/channels`, { replace: true });
        } catch (e) {
          message.error(extractErrorMessage(e));
        }
      },
    });

  return (
    <div className="ch-grid">
      <div className="ch-main">
        <section className="v2-card ch-hero">
          <ChBadge kind={c.platform as 'telegram' | 'max'} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{c.bot_display_name || c.title}</span>
              <span className={tg ? 'ch-pill is-tg' : 'ch-pill is-max'}>{where}</span>
              <StatusPill status={c.status} />
            </div>
            <div className="v2-xs v2-muted">
              @{c.bot_username} · подключён {dateRu(c.created_at, true)}
            </div>
          </div>
          {link && (
            <a href={link} target="_blank" rel="noreferrer noopener" className="v2-btn">
              Открыть бота в {where} ↗
            </a>
          )}
        </section>

        {c.status === 'disabled' ? (
          <section className="v2-card ch-card" role="alert" style={{ borderColor: '#FECACA' }}>
            <h2 className="v2-h2" style={{ color: '#B91C1C' }}>
              Бот не работает
            </h2>
            <div className="v2-small" style={{ color: 'var(--v2-text-2)' }}>
              {where} перестал принимать токен бота — например, токен сменили или бота удалили.
              Отключите бота здесь и подключите заново с новым токеном.
            </div>
          </section>
        ) : (
          <section
            className="v2-card ch-card"
            style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}
          >
            <div style={{ flex: 1 }}>
              <h2 className="v2-h2" id="h-on">
                Бот отвечает клиентам
              </h2>
              <div className="v2-small" style={{ color: 'var(--v2-muted)', marginTop: 4 }}>
                Выключите, чтобы поставить на паузу: бот перестанет отвечать, но останется
                подключённым. Включить обратно — одним нажатием.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-labelledby="h-on"
              disabled={busy}
              className={on ? 'st-switch is-on' : 'st-switch'}
              onClick={toggle}
            >
              <span />
            </button>
          </section>
        )}

        <section className="v2-card ch-card" aria-labelledby="h-stat">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 12,
            }}
          >
            <h2 id="h-stat" className="v2-h2">
              Статистика
            </h2>
            <Link to={`/assistants/${assistantId}/dialogs?channel=${c.id}`} className="ch-link">
              Диалоги этого бота →
            </Link>
          </div>
          <div className="ch-stats">
            <div>
              <div className="v2-xs v2-muted" title="Вместе с теми, кто отписался от бота">
                Диалогов всего
              </div>
              <b>{num(c.dialogs)}</b>
            </div>
            <div>
              <div className="v2-xs v2-muted">Сообщений всего</div>
              <b>{num(c.messages_total)}</b>
            </div>
            <div>
              <div className="v2-xs v2-muted">Сообщений за сутки</div>
              <b>{num(c.messages_24h)}</b>
            </div>
          </div>
        </section>
      </div>

      <div className="ch-side">
        <section className="v2-card ch-card" aria-labelledby="h-subs">
          <h2 id="h-subs" className="v2-h2">
            Подписчики и рассылки
          </h2>
          <div className="v2-small" style={{ color: 'var(--v2-text-2)' }}>
            Кто написал боту, становится подписчиком — им можно отправлять рассылки.
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="v2-small v2-muted">Подписчиков</span>
            <b>{num(c.subscribers)}</b>
          </div>
          <Link to={`/assistants/${assistantId}/broadcasts`} className="ch-link">
            Открыть рассылки →
          </Link>
        </section>
        <section className="v2-card ch-card ch-danger" aria-labelledby="h-off">
          <h2 id="h-off" className="v2-h2">
            Отключить бота
          </h2>
          <div className="v2-small" style={{ color: 'var(--v2-text-2)' }}>
            {/* ⚠️ Макет обещал «переписка сохранится», но сейчас отключение удаляет
                диалоги бота (каскад в БД) — пишем честно, решение за Игорем. */}
            Бот перестанет отвечать, а переписка с ним удалится из «Диалогов». Подключить снова
            можно в любой момент — статистика начнётся с нуля.
          </div>
          <button type="button" className="v2-btn ch-btn-danger" onClick={disconnect}>
            Отключить
          </button>
        </section>
      </div>
    </div>
  );
}
